import React, { useState, useEffect, useMemo } from "react";
import { 
  Sparkles, 
  Save, 
  CheckCircle2, 
  RefreshCw, 
  AlertTriangle, 
  X, 
  Copy, 
  Check, 
  Bot, 
  Globe,
  FileText
} from "lucide-react";
import { pb } from "../../services/pocketbase";
import { getBaseSku } from "../../utils/skuUtils";

import { aiPipelineService, isLikelyAdTypePrompt } from "../../services/aiPipelineService";

// Formatação amigável dos dados da Ficha Técnica
const formatFichaTecnicaText = (ft) => {
  if (!ft) return "";
  let text = "Especificações Ficha Técnica:\n";
  if (ft.peso_bruto_g || ft.peso_gramas) text += `- Peso Bruto: ${ft.peso_bruto_g || ft.peso_gramas}g\n`;
  if (ft.peso_liquido_g) text += `- Peso Líquido: ${ft.peso_liquido_g}g\n`;
  if (ft.liga_metal) text += `- Liga/Metal: ${ft.liga_metal}\n`;
  if (ft.tempo_producao_minutos) text += `- Tempo de Produção: ${ft.tempo_producao_minutos} min\n`;
  if (ft.qtd_pedras) text += `- Qtd de Pedras: ${ft.qtd_pedras}\n`;
  if (Array.isArray(ft.detalhes_pedras) && ft.detalhes_pedras.length > 0) {
    text += `- Detalhes das Pedras:\n`;
    ft.detalhes_pedras.forEach((p) => {
      text += `  • ${p.qtd || 1}x ${p.tipo || "Pedra"} (${p.tamanho || "N/D"})\n`;
    });
  }
  return text;
};

/**
 * GeradorAnuncioIAModal.js — IA no Lugar Certo (Fase P3.3)
 *
 * Gera o anúncio e SEO do produto que está sendo visto usando o mesmo motor
 * de `ia_configs` e `ia_prompts` com provedores Google, OpenAI ou Anthropic.
 * 
 * Agora expandido com os 5 campos canônicos do site, HTML viewer/previewer
 * e edição manual persistente.
 */
export default function GeradorAnuncioIAModal({ isOpen, onClose, skuPai, item, onSuccess }) {
  const [produtoNome, setProdutoNome] = useState("");
  const [categoria, setCategoria] = useState("Anel");
  const [tipoAnuncio, setTipoAnuncio] = useState("Site");
  const [modelo, setModelo] = useState("");
  const [descricaoInput, setDescricaoInput] = useState("");

  // 5 Campos do Catálogo
  const [pageTitle, setPageTitle] = useState("");
  const [descriptionSmall, setDescriptionSmall] = useState("");
  const [pageDescription, setPageDescription] = useState("");
  const [pageKeywords, setPageKeywords] = useState("");
  const [descriptionFull, setDescriptionFull] = useState("");

  const [originalFields, setOriginalFields] = useState({
    page_title: "",
    description_small: "",
    page_description: "",
    page_keywords: "",
    description_full: ""
  });
  const [hasGenerated, setHasGenerated] = useState(false);

  const [loading, setLoading] = useState(false);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [currentStep, setCurrentStep] = useState("");
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [activeProvider, setActiveProvider] = useState(null);
  const [prompts, setPrompts] = useState([]);
  const [catalogRecord, setCatalogRecord] = useState(null);

  // Aba da descrição completa (HTML vs Preview)
  const [descriptionTab, setDescriptionTab] = useState("editor"); // "editor" | "preview"

  // Preenche dados contextuais e carrega o catálogo ao abrir
  useEffect(() => {
    if (isOpen && skuPai) {
      const name = item?.name || item?.full_data?.name || `Produto ${skuPai}`;
      const desc = item?.full_data?.description_full || item?.full_data?.description || item?.description || "";
      const mod = item?.full_data?.model || item?.full_data?.modelo || item?.model || "";

      setProdutoNome(name);
      setModelo(mod);
      setResult(null);
      setErrorMsg("");
      setHasGenerated(false);

      // Deriva categoria inicial pelo prefixo de SKU
      const prefix = skuPai.split("-")[0]?.toUpperCase() || "";
      if (prefix === "RIN" || prefix === "ALI" || prefix === "DIR") setCategoria("Anel");
      else if (prefix === "PIN" || prefix === "MED") setCategoria("Pingente");
      else if (prefix === "BER") setCategoria("Berloque");
      else if (prefix === "COR" || prefix === "PUL") setCategoria("Corrente/Pulseira");
      else setCategoria("Anel");

      // Função assíncrona para buscar Ficha Técnica e registro atual do Catálogo
      const fetchProductAndCatalogData = async () => {
        setLoadingCatalog(true);
        let ftText = "";

        // 1. Busca dados adicionais e Ficha Técnica (usando SKU pai limpo)
        const cleanPai = getBaseSku(skuPai);
        try {
          const dadosRecords = await pb.collection("produto_dados").getFullList({
            filter: `sku_pai = "${cleanPai}"`
          });
          if (dadosRecords.length > 0 && dadosRecords[0].ficha_tecnica) {
            ftText = formatFichaTecnicaText(dadosRecords[0].ficha_tecnica);
          }
        } catch (e) {
          console.warn("Aviso ao buscar Ficha Técnica:", e);
        }

        // 2. Busca registro atual no Catálogo (full_data)
        try {
          const catRecord = await pb.collection("catalog").getFirstListItem(`sku = "${skuPai}"`);
          setCatalogRecord(catRecord);
          const fd = catRecord?.full_data || {};

          const orig = {
            page_title: fd.page_title || item?.full_data?.page_title || name || "",
            description_small: fd.description_small || item?.full_data?.description_small || "",
            page_description: fd.page_description || item?.full_data?.page_description || "",
            page_keywords: fd.page_keywords || item?.full_data?.page_keywords || "",
            description_full: fd.description_full || fd.description || desc || ""
          };
          setOriginalFields(orig);

          setPageTitle(orig.page_title);
          setDescriptionSmall(orig.description_small);
          setPageDescription(orig.page_description);
          setPageKeywords(orig.page_keywords);
          setDescriptionFull(orig.description_full);
        } catch (e) {
          console.warn("Item do catálogo não encontrado para este SKU em PocketBase:", e);
          setCatalogRecord(null);
          
          const orig = {
            page_title: item?.full_data?.page_title || name || "",
            description_small: item?.full_data?.description_small || "",
            page_description: item?.full_data?.page_description || "",
            page_keywords: item?.full_data?.page_keywords || "",
            description_full: item?.full_data?.description_full || item?.full_data?.description || desc || ""
          };
          setOriginalFields(orig);

          // Fallbacks com dados da memória
          setPageTitle(orig.page_title);
          setDescriptionSmall(orig.description_small);
          setPageDescription(orig.page_description);
          setPageKeywords(orig.page_keywords);
          setDescriptionFull(orig.description_full);
        } finally {
          // Preenche o campo de texto/contexto com a Ficha Técnica se existir
          setDescricaoInput(prev => {
            if (ftText) return ftText;
            return desc || `Joia confeccionada em Prata 925 / Ouro. Código SKU: ${skuPai}. Excelente acabamento.`;
          });
          setLoadingCatalog(false);
        }
      };

      fetchProductAndCatalogData();
    }
  }, [isOpen, skuPai, item]);

  // Carrega Provedor Ativo (ia_configs) e Prompts (ia_prompts)
  useEffect(() => {
    if (!isOpen) return;

    const loadIaConfig = async () => {
      try {
        const [configRecords, promptRecords] = await Promise.all([
          pb.collection("ia_configs").getFullList({ filter: "ativo = true", limit: 1 }),
          pb.collection("ia_prompts").getFullList({ sort: "ordem" })
        ]);

        if (configRecords.length > 0) {
          setActiveProvider(configRecords[0]);
        }
        setPrompts((promptRecords || []).filter(p => p.ativo !== false));
      } catch (err) {
        console.warn("Erro ao carregar configurações de IA em P3.3:", err);
      }
    };

    loadIaConfig();
    window.addEventListener("ia-prompts-updated", loadIaConfig);
    return () => window.removeEventListener("ia-prompts-updated", loadIaConfig);
  }, [isOpen]);

  const availableCategories = useMemo(() => {
    return prompts
      .filter(p => p.tipo === "categoria" && !isLikelyAdTypePrompt(p))
      .map(p => p.contexto || p.nome)
      .filter((v, i, a) => v && a.indexOf(v) === i);
  }, [prompts]);

  const availableAdTypes = useMemo(() => {
    return prompts
      .filter(p => p.tipo === "tipo_anuncio" || isLikelyAdTypePrompt(p))
      .map(p => p.contexto || p.nome)
      .filter((v, i, a) => v && a.indexOf(v) === i);
  }, [prompts]);

  if (!isOpen) return null;

  // Executa o Pipeline de IA via aiPipelineService Centralizado
  const runAiPipeline = async () => {
    if (!skuPai) return alert("SKU inválido.");
    if (!produtoNome.trim()) return alert("Nome do produto é obrigatório.");

    setErrorMsg("");
    setLoading(true);
    setResult(null);

    try {
      const response = await aiPipelineService.runProductPipeline({
        sku: skuPai.trim().toUpperCase(),
        nome: produtoNome.trim(),
        categoria,
        modelo,
        descricaoTecnica: descricaoInput,
        tipoAnuncio,
        onStep: (stepMsg) => setCurrentStep(stepMsg)
      });

      const parsed = response.result;
      setResult(parsed);

      setPageTitle(parsed.page_title);
      setDescriptionSmall(parsed.description_small);
      setPageDescription(parsed.page_description);
      setPageKeywords(parsed.page_keywords);
      setDescriptionFull(parsed.description_full);
      setHasGenerated(true);
    } catch (err) {
      console.error("Erro no gerador de IA:", err);
      setErrorMsg(err.message || "Erro ao conectar com o provedor de IA.");
    } finally {
      setLoading(false);
      setCurrentStep("");
    }
  };

  // Salva os campos editados nas coleções catalog e produto_dados
  const handleSaveAdToProduct = async () => {
    if (!skuPai) return;

    setSaving(true);
    try {
      // 1. Resolve ou busca o registro atual no catálogo
      let catRecord = catalogRecord;
      if (!catRecord) {
        try {
          catRecord = await pb.collection("catalog").getFirstListItem(`sku = "${skuPai}"`);
        } catch (e) {
          console.warn("Item não cadastrado na tabela de catálogo do PocketBase.");
        }
      }

      const currentFullData = catRecord?.full_data || {};
      const updatedFullData = {
        ...currentFullData,
        page_title: pageTitle,
        description_small: descriptionSmall,
        page_description: pageDescription,
        page_keywords: pageKeywords,
        description_full: descriptionFull,
        name: produtoNome,
        model: modelo
      };

      const catalogPayload = {
        sku: skuPai,
        name: produtoNome,
        full_data: updatedFullData
      };

      if (catRecord) {
        const updated = await pb.collection("catalog").update(catRecord.id, catalogPayload);
        setCatalogRecord(updated);
      } else {
        const created = await pb.collection("catalog").create(catalogPayload);
        setCatalogRecord(created);
      }

      // 2. Atualiza links_legado em produto_dados
      const existingDados = await pb.collection("produto_dados").getFullList({
        filter: `sku_pai = "${skuPai}"`
      });

      if (existingDados.length > 0) {
        const linksLegado = existingDados[0].links_legado || {};
        await pb.collection("produto_dados").update(existingDados[0].id, {
          links_legado: {
            ...linksLegado,
            generatedAd: {
              titulo: pageTitle,
              descricao: descriptionFull,
              generatedAt: new Date().toISOString()
            }
          }
        });
      }

      alert("Dados do catálogo e SEO salvos com sucesso!");
      if (onSuccess) onSuccess(descriptionFull);
      onClose();
    } catch (err) {
      alert("Erro ao salvar anúncio e campos no produto: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-6xl overflow-hidden flex flex-col max-h-[90vh] animate-scale-in">
        
        {/* HEADER */}
        <div className="bg-slate-900 text-white px-6 py-4 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-r from-amber-500 to-purple-600 rounded-xl text-white">
              <Sparkles size={20} />
            </div>
            <div>
              <h2 className="text-base font-bold tracking-tight">Gerador de Anúncios com IA & Editor do Catálogo</h2>
              <p className="text-xs text-slate-400 font-mono">Produto Contextual (SKU Pai): {skuPai}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* CORPO DO MODAL EM 2 COLUNAS */}
        <div className="flex-1 overflow-y-auto p-6 grid grid-cols-1 lg:grid-cols-2 gap-6 min-h-0">
          
          {/* COLUNA ESQUERDA: PIPELINE E PARÂMETROS DA IA */}
          <div className="flex flex-col space-y-4">
            <h3 className="font-bold text-slate-800 text-sm border-b pb-2 flex items-center gap-1.5 uppercase tracking-wider">
              <Bot size={16} className="text-purple-600" />
              Parâmetros & Geração de IA
            </h3>

            {/* PROVEDOR DE IA ATIVO BADGE */}
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 flex items-center justify-between text-xs text-purple-900">
              <div className="flex items-center gap-2">
                <Bot size={16} className="text-purple-600" />
                <span>Provedor Ativo: <strong>{activeProvider?.nome_exibicao || "Carregando..."}</strong> ({activeProvider?.provedor || "IA"})</span>
              </div>
              <span className="text-[11px] bg-purple-100 text-purple-800 px-2 py-0.5 rounded font-mono">
                {activeProvider?.modelo_padrao || "gemini-2.5-flash"}
              </span>
            </div>

            {errorMsg && (
              <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-xs flex items-center gap-2">
                <AlertTriangle size={16} className="shrink-0" />
                <span>{errorMsg}</span>
              </div>
            )}

            {/* FORMULÁRIO DE GERAÇÃO */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
              <div>
                <label className="block font-bold text-slate-700 mb-1 uppercase tracking-wider text-[10px]">Nome base do Produto</label>
                <input
                  type="text"
                  value={produtoNome}
                  onChange={(e) => setProdutoNome(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-300 rounded-lg p-2 font-semibold text-slate-800 focus:bg-white outline-none"
                />
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1 uppercase tracking-wider text-[10px]">Categoria Anúncio</label>
                <select
                  value={categoria}
                  onChange={(e) => setCategoria(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-300 rounded-lg p-2 font-semibold text-slate-800 focus:bg-white outline-none cursor-pointer"
                >
                  {availableCategories.length > 0 ? (
                    availableCategories.map(c => <option key={c} value={c}>{c}</option>)
                  ) : (
                    <>
                      <option value="Anel">Anel</option>
                      <option value="Pingente">Pingente</option>
                      <option value="Berloque">Berloque</option>
                      <option value="Corrente/Pulseira">Corrente/Pulseira</option>
                    </>
                  )}
                </select>
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1 uppercase tracking-wider text-[10px]">Canal / Destino</label>
                <select
                  value={tipoAnuncio}
                  onChange={(e) => setTipoAnuncio(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-300 rounded-lg p-2 font-semibold text-slate-800 focus:bg-white outline-none cursor-pointer"
                >
                  {availableAdTypes.length > 0 ? (
                    availableAdTypes.map(t => <option key={t} value={t}>{t}</option>)
                  ) : (
                    <>
                      <option value="Site">Site E-Commerce</option>
                      <option value="Mercado Livre">Mercado Livre</option>
                      <option value="Instagram">Instagram / Redes Sociais</option>
                    </>
                  )}
                </select>
              </div>
            </div>

            <div className="flex-1 flex flex-col min-h-[180px]">
              <label className="font-bold text-slate-700 mb-1 uppercase tracking-wider text-[10px] flex items-center gap-1">
                <FileText size={13} className="text-slate-500" />
                Especificações Técnicas da Peça (Contexto da Ficha Técnica)
              </label>
              <textarea
                value={descricaoInput}
                onChange={(e) => setDescricaoInput(e.target.value)}
                placeholder="Insira detalhes técnicos, peso do metal, ligas ou pedras que a IA usará para gerar os dados..."
                className="w-full flex-1 min-h-[160px] bg-slate-50 border border-slate-300 rounded-lg p-3 font-medium text-slate-800 focus:bg-white outline-none text-xs leading-relaxed"
              />
            </div>

            {/* BOTÃO DISPARAR PIPELINE IA */}
            <button
              onClick={runAiPipeline}
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-xl font-bold text-xs shadow-md transition-all flex items-center justify-center gap-2 disabled:opacity-50 active:scale-[0.99] select-none"
            >
              {loading ? (
                <>
                  <RefreshCw size={16} className="animate-spin" />
                  <span>{currentStep || "Gerando com IA..."}</span>
                </>
              ) : (
                <>
                  <Sparkles size={16} />
                  <span>Gerar Dados & HTML com IA</span>
                </>
              )}
            </button>
          </div>

          {/* COLUNA DIREITA: CAMPOS DO CATALOGO (SITE & SEO) */}
          <div className="flex flex-col space-y-4 border-t lg:border-t-0 lg:border-l border-slate-200 pt-6 lg:pt-0 lg:pl-6">
            <h3 className="font-bold text-slate-800 text-sm border-b pb-2 flex items-center gap-1.5 uppercase tracking-wider">
              <Globe size={16} className="text-blue-500" />
              Campos do Catálogo (Site & SEO)
            </h3>

            {loadingCatalog ? (
              <div className="flex-1 flex flex-col items-center justify-center py-20 text-slate-400 gap-2">
                <RefreshCw className="animate-spin text-blue-500" size={24} />
                <span className="text-xs">Sincronizando dados atuais do e-commerce...</span>
              </div>
            ) : (
              <div className="space-y-3 flex-1 flex flex-col">
                {/* FUNÇÃO AUXILIAR DE COMPARAÇÃO */}
                {(() => {
                  const isFieldModified = (fieldName, currentValue) => {
                    return (currentValue || "").trim() !== (originalFields[fieldName] || "").trim();
                  };

                  return (
                    <>
                      {/* BANNER NOTIFICAÇÃO IA */}
                      {hasGenerated && (
                        <div className="bg-purple-50 border border-purple-200 text-purple-900 rounded-xl p-3 text-xs flex items-center justify-between shadow-sm animate-fade-in shrink-0">
                          <div className="flex items-center gap-2">
                            <Sparkles size={16} className="text-purple-600 animate-pulse shrink-0" />
                            <span>
                              <strong>Campos carregados com IA!</strong> Revise as alterações destacadas em roxo abaixo.
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => setHasGenerated(false)}
                            className="text-purple-600 hover:text-purple-800 font-bold ml-2 text-[10px] uppercase tracking-wider"
                          >
                            Fechar
                          </button>
                        </div>
                      )}

                      {/* INPUTS DO CATÁLOGO */}
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label className="block font-bold text-slate-700 uppercase tracking-wider text-[10px]">Título da Página (page_title)</label>
                          {isFieldModified("page_title", pageTitle) && (
                            <span className="text-[9px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full font-bold flex items-center gap-0.5 animate-pulse">
                              <Sparkles size={8} /> Modificado
                            </span>
                          )}
                        </div>
                        <input
                          type="text"
                          value={pageTitle}
                          onChange={(e) => setPageTitle(e.target.value)}
                          className={`w-full bg-slate-50 border rounded-lg p-2 font-semibold text-slate-800 focus:bg-white outline-none text-xs transition-all ${
                            isFieldModified("page_title", pageTitle)
                              ? "border-purple-400 bg-purple-50/20 shadow-sm"
                              : "border-slate-300"
                          }`}
                          placeholder="Título otimizado para e-commerce..."
                        />
                      </div>

                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label className="block font-bold text-slate-700 uppercase tracking-wider text-[10px]">Resumo Comercial (description_small)</label>
                          {isFieldModified("description_small", descriptionSmall) && (
                            <span className="text-[9px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full font-bold flex items-center gap-0.5 animate-pulse">
                              <Sparkles size={8} /> Modificado
                            </span>
                          )}
                        </div>
                        <textarea
                          rows="2"
                          value={descriptionSmall}
                          onChange={(e) => setDescriptionSmall(e.target.value)}
                          className={`w-full bg-slate-50 border rounded-lg p-2 font-medium text-slate-800 focus:bg-white outline-none resize-none text-xs leading-relaxed transition-all ${
                            isFieldModified("description_small", descriptionSmall)
                              ? "border-purple-400 bg-purple-50/20 shadow-sm"
                              : "border-slate-300"
                          }`}
                          placeholder="Breve descrição comercial do anúncio..."
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <label className="block font-bold text-slate-700 uppercase tracking-wider text-[10px]">Meta Descrição SEO (page_description)</label>
                            {isFieldModified("page_description", pageDescription) && (
                              <span className="text-[9px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full font-bold flex items-center gap-0.5 animate-pulse">
                                <Sparkles size={8} /> Modificado
                              </span>
                            )}
                          </div>
                          <textarea
                            rows="2"
                            value={pageDescription}
                            onChange={(e) => setPageDescription(e.target.value)}
                            maxLength={160}
                            className={`w-full bg-slate-50 border rounded-lg p-2 font-medium text-slate-800 focus:bg-white outline-none resize-none text-xs transition-all ${
                              isFieldModified("page_description", pageDescription)
                                ? "border-purple-400 bg-purple-50/20 shadow-sm"
                                : "border-slate-300"
                            }`}
                            placeholder="Meta descrição de busca..."
                          />
                          <div className="text-[9px] text-slate-400 text-right mt-0.5 font-mono">{pageDescription.length}/160 caracteres</div>
                        </div>

                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <label className="block font-bold text-slate-700 uppercase tracking-wider text-[10px]">Meta Keywords (page_keywords)</label>
                            {isFieldModified("page_keywords", pageKeywords) && (
                              <span className="text-[9px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full font-bold flex items-center gap-0.5 animate-pulse">
                                <Sparkles size={8} /> Modificado
                              </span>
                            )}
                          </div>
                          <textarea
                            rows="2"
                            value={pageKeywords}
                            onChange={(e) => setPageKeywords(e.target.value)}
                            className={`w-full bg-slate-50 border rounded-lg p-2 font-mono text-slate-800 focus:bg-white outline-none resize-none text-[11px] transition-all ${
                              isFieldModified("page_keywords", pageKeywords)
                                ? "border-purple-400 bg-purple-50/20 shadow-sm"
                                : "border-slate-300"
                            }`}
                            placeholder="anel solitario, prata 925, joias..."
                          />
                        </div>
                      </div>

                      {/* DESCRIÇÃO HTML COM VISUALIZADOR/PREVIEWER */}
                      <div className="flex-1 flex flex-col min-h-[220px]">
                        <div className="flex items-center justify-between bg-slate-100 p-1.5 rounded-t-lg border border-slate-300 border-b-0">
                          <div className="flex items-center gap-1.5 pl-2">
                            <span className="font-bold text-slate-700 text-[10px] uppercase tracking-wider">Descrição Completa (HTML)</span>
                            {isFieldModified("description_full", descriptionFull) && (
                              <span className="text-[9px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full font-bold flex items-center gap-0.5 animate-pulse">
                                <Sparkles size={8} /> Modificado
                              </span>
                            )}
                          </div>
                          <div className="flex gap-1 text-[11px] font-bold">
                            <button
                              type="button"
                              onClick={() => setDescriptionTab("editor")}
                              className={`px-3 py-1 rounded transition-all ${
                                descriptionTab === "editor"
                                  ? "bg-white text-slate-800 shadow-sm border border-slate-200"
                                  : "text-slate-500 hover:text-slate-800"
                              }`}
                            >
                              Código HTML
                            </button>
                            <button
                              type="button"
                              onClick={() => setDescriptionTab("preview")}
                              className={`px-3 py-1 rounded transition-all ${
                                descriptionTab === "preview"
                                  ? "bg-white text-slate-800 shadow-sm border border-slate-200"
                                  : "text-slate-500 hover:text-slate-800"
                              }`}
                            >
                              Página Montada (Visualizar)
                            </button>
                          </div>
                        </div>

                        {descriptionTab === "editor" ? (
                          <textarea
                            value={descriptionFull}
                            onChange={(e) => setDescriptionFull(e.target.value)}
                            className={`w-full flex-1 min-h-[160px] text-slate-100 font-mono text-[11px] p-3 rounded-b-lg border outline-none focus:ring-1 focus:ring-blue-500 transition-all leading-normal ${
                              isFieldModified("description_full", descriptionFull)
                                ? "border-purple-400 bg-slate-950 focus:bg-slate-950"
                                : "border-slate-300 bg-slate-900 focus:bg-slate-950"
                            }`}
                            placeholder="<p>Formate as especificações ricas do seu anúncio em HTML aqui...</p>"
                          />
                        ) : (
                          <div className="preview-container w-full flex-1 min-h-[160px] bg-white text-slate-800 text-sm leading-relaxed p-4 rounded-b-lg border border-slate-300 overflow-y-auto max-h-[250px]">
                            <style>{`
                              .preview-container h1 { font-size: 1.5rem; font-weight: bold; margin-bottom: 0.5rem; color: #1e293b; border-bottom: 1px solid #e2e8f0; padding-bottom: 0.25rem; }
                              .preview-container h2 { font-size: 1.25rem; font-weight: bold; margin-bottom: 0.5rem; color: #334155; }
                              .preview-container h3 { font-size: 1.1rem; font-weight: bold; margin-bottom: 0.5rem; color: #475569; }
                              .preview-container p { margin-bottom: 0.75rem; color: #334155; }
                              .preview-container ul { list-style-type: disc; padding-left: 1.5rem; margin-bottom: 0.75rem; color: #334155; }
                              .preview-container ol { list-style-type: decimal; padding-left: 1.5rem; margin-bottom: 0.75rem; color: #334155; }
                              .preview-container li { margin-bottom: 0.25rem; }
                              .preview-container strong { font-weight: bold; color: #0f172a; }
                              .preview-container em { font-style: italic; }
                            `}</style>
                            {descriptionFull ? (
                              <div dangerouslySetInnerHTML={{ __html: descriptionFull }} />
                            ) : (
                              <p className="text-slate-400 italic text-xs">Nenhum conteúdo HTML gerado ou editado ainda.</p>
                            )}
                          </div>
                        )}
                      </div>
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        </div>

        {/* FOOTER */}
        <div className="bg-slate-50 border-t border-slate-200 px-6 py-4 flex items-center justify-between shrink-0">
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 border border-slate-300 hover:bg-slate-100 rounded-xl text-slate-700 text-xs font-semibold shadow-sm transition-all">
              Cancelar
            </button>
            {descriptionFull && (
              <button
                type="button"
                onClick={() => copyToClipboard(descriptionFull)}
                className="px-4 py-2 bg-slate-200 hover:bg-slate-300 rounded-xl text-slate-800 text-xs font-semibold shadow-sm transition-all flex items-center gap-1"
              >
                {copied ? <Check size={13} className="text-emerald-600" /> : <Copy size={13} />}
                <span>{copied ? "HTML Copiado!" : "Copiar HTML da Descrição"}</span>
              </button>
            )}
          </div>

          <button
            onClick={handleSaveAdToProduct}
            disabled={saving || loadingCatalog}
            className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition-all shadow-md flex items-center gap-1.5 disabled:opacity-50 active:scale-[0.99] select-none"
          >
            {saving ? (
              <>
                <RefreshCw size={15} className="animate-spin" />
                <span>Gravando no Catálogo...</span>
              </>
            ) : (
              <>
                <Save size={15} />
                <span>Salvar Modificações no Catálogo</span>
              </>
            )}
          </button>
        </div>

      </div>
    </div>
  );
}
